import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocaleService } from '../locale-service/locale.service';
import { SupportedLocale } from '../locale-service/locale.model';
import { matchSupportedLocale } from '../locale-service/locale.util';

@Injectable({
    providedIn: 'root'
})
export class TranslatorService {
    constructor(
        public translate: TranslateService,
        private localeService: LocaleService,
        @Inject(DOCUMENT) private document: Document,
    ) {
        if (!translate.getFallbackLang()) {
            translate.setFallbackLang(this.localeService.getDefaultCulture());
        }
    }

    /**
     * Apply culture/ui language. Accepts BCP-47 culture or legacy bare language codes.
     * @returns culture tag applied
     */
    setDefaultLanguage(lang: string = ''): string {
        const allowlist = this.localeService.getAvailableLocales();
        const matched = matchSupportedLocale(lang, allowlist, this.localeService.getDefaultCulture());
        this.document.documentElement.lang = matched.culture;
        this.document.documentElement.dir = matched.dir;
        const elem = this.document.getElementsByClassName('section-container')[0] as HTMLElement | undefined;
        if (elem) {
            elem.dir = matched.dir;
        }
        return matched.culture;
    }

    async useLanguage(lang: string = '', fireEvents: boolean = true): Promise<void> {
        await this.localeService.setCulture(lang || this.localeService.getCulture(), {
            fireTranslateEvents: fireEvents,
            persistUserData: true,
        });
    }

    getAvailableLanguages(): SupportedLocale[] {
        return this.localeService.getAvailableLocales();
    }

    /** Returns the active BCP-47 culture (compat: formerly bare language code). */
    getCurrentLanguage(): string {
        return this.localeService.getCulture();
    }

    getIsRTL(): boolean {
        return this.localeService.getIsRTL();
    }

    /** Active BCP-47 culture for Intl formatting. */
    getCulture(): string {
        return this.localeService.getCulture();
    }

    isUSCulture(): boolean {
        return this.localeService.isUSCulture();
    }

    isGBCulture(): boolean {
        return this.localeService.isGBCulture();
    }

    async refreshRTL() {
       if (this.getIsRTL()) {
            const rememberLang = this.getCurrentLanguage();
            await this.useLanguage('en', false);
            await this.useLanguage(rememberLang, false);
        }
    }

    translateText(key: string | string[], interpolateParams?: object): string {
        return this.translate.instant(key, interpolateParams);
    }
}
